import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  View,
  Text,
  ActionButton,
  Image,
  Flex,
  TooltipTrigger,
  Tooltip,
  MenuTrigger,
  Menu,
  Item
} from '@adobe/react-spectrum';
import { useSelector, useDispatch } from 'react-redux';
import styles from './TimelineCard.module.scss';
import { PARKING_LOT_GRID_COLUMN_PX } from 'app/components/RoadmapCard/parkingLotLayoutConstants';
import './ContextMenu.module.scss';
// Import icons
import ExpandIcon from '../../assets/icons/features_card_collapsed.svg';
import CheckMarkCircleIcon from 'app/assets/svgIcons/CheckMarkCircleIcon';
// Import shared category icon utility
import { getProductIcons } from '../../../utils/categoryIconUtils';
import AdobeIcon from '../../assets/icons/apps_icons/AdobeIcon.svg';
import alertTriangle from '../../../images/alert_traingle.svg';
import { useHomeSlice } from 'app/pages/Home/slice';
import {
  selectCollapseAll,
  selectHighlightedIssue,
  selectHome,
  selectUnNestAll,
  selectUnnestParentIds,
  selectParkingLot,
  selectUpdateSuccessAlert
} from 'app/pages/Home/slice/selectors';
import moment from 'moment';
import ChildCard from '../ChildCard';
import FeatureCard from '../FeatureCard';
import {
  saveUserPreferences,
  getUserPreferences,
  openDocument,
  isBetaLabel,
  isPrivateBetaLabel,
  isBetaOrPrivateBetaLabel,
  getReleasePhaseIcon,
  getQuarterEndDate,
  getMonthEndDate,
  getEffectiveStatus,
  normalizeStatus
} from 'utils/helpers';
// Import custom legend icons for risk
import { LegendRiskLowIcon } from 'app/assets/legend/LegendRiskLowIcon';
import { LegendRiskModerateIcon } from 'app/assets/legend/LegendRiskModerateIcon';
import { LegendRiskHighIcon } from 'app/assets/legend/LegendRiskHighIcon';
import { LegendInPlanningIcon } from 'app/assets/legend/LegendInPlanningIcon';
import { LegendBetaMiniIcon } from 'app/assets/legend/LegendBetaMiniIcon';
import { LegendPrivateBetaMiniIcon } from 'app/assets/legend/LegendPrivateBetaMiniIcon';
import S2IconMore from '../../assets/icons/S2_Icon_More_20_N.svg';
import RevenueImpactIcon from './RevenueImpactIcon';
import { EditEventModal } from '../RsdForms/EditEventModal';
import { useSlideoutUrl } from '../../hooks/useSlideoutUrl';
import {
  isEolMoment,
  normalizeRiskLevelToStored,
  RISK_LEVELS,
  STATUS_VALUES,
  MOMENT_TYPES
} from 'utils/constants';
import { getTimelineDisplayDates } from 'utils/timelineDisplayDates';
import { getRtmPhasedTitleParts } from 'utils/rtmPhaseDisplayTitle';
import { CardTitleDisplay } from './CardTitleDisplay';
import { formatEolPhaseDisplayTitle } from 'utils/eolPhaseTitlePrefix';
import { updateMoment } from '../RsdForms/api';
import { reorderReleaseFeatures } from '../../services/featuresApi';
import { EnhancedAlertBanner } from '../RsdForms/EnhancedAlertBanner';
import { useEventKeyDatePermissions } from '../../utils/rbacHandler';
import { DateTooltip } from './DateTooltip';
import { selectTimelineIconsVisible } from 'app/components/MonthlyTimeline/store/selectors';
import {
  deleteMoment,
  clearEventsCache,
  clearParentMomentsCache
} from '../../services/momentsApi';
import { ToastQueue } from '@adobe/react-spectrum';
import { RiskLevelTooltipContent } from '../shared/RiskLevelTooltipContent';
import { ProductIconCompact } from './ProductIconCompact';
import {
  useReorderable,
  AFTER_LAST_DROP_TARGET
} from '../../hooks/useReorderable';

const DRAG_DROP_REORDER_ENABLED = true;
const REORDER_DEBOUNCE_MS = 600;

interface TimelineCardProps {
  releasePhaseId?: string;
  wholeCard: any;
  projectType: string;
  title: string;
  categories?: string[];
  products?: string[];
  labels?: string[];
  releasePhase?: string; // Change from labels to releasePhase
  riskLevel?: string; // Just use string type
  childRelationships?: any[];
  features?: any[]; // Add features array for feature cards
  status?: string;
  type?: string;
  isCompact?: boolean;
  reportingSummary?: string;
  lastUpdated?: string;
  dates?: {
    targetStart?: string;
    targetEnd?: string;
    targetMonth?: string;
    targetQuarter?: string;
    targetEndMonth?: string;
    // Secondary dates for timeline display
    releaseDate?: string; // Primary secondary date field
    releaseMonth?: string;
    releaseQuarter?: string;
    effectiveDate?: string; // Fallback secondary date field
    effectiveMonth?: string;
    effectiveQuarter?: string;
    // Price Changes and Offers: customer notification date
    customerNotificationDate?: string;
    customerNotificationMonth?: string; // API field name
    customerNotificationDateMonth?: string; // legacy alias
    customerNotificationQuarter?: string; // API field name
    customerNotificationDateQuarter?: string; // legacy alias
    // Separate date status fields for each date type
    targetDateStatus?: string; // TBD or N/A for target dates
    releaseDateStatus?: string; // TBD or N/A for release dates
    effectiveDateStatus?: string; // TBD or N/A for effective dates
    customerNotificationDateStatus?: string;
  };
  relationships?: Array<{ type: string; value: string }>;
  issueId?: string;
  isQuarterExpanded;
  links?: Array<{ type: string; reference: string }>;
  priority?: string;
  showOnTimeline?: boolean; // New prop to control which dates to use
  // Add mapped date range properties
  scheduleRange?: any;
  dateRange?: any;
  // Add relationship object from API response
  relationship?: {
    badgecount?: string;
    releaseHierarchyPaths?: Array<{
      parent: string;
      issue: string;
      phase: string;
      phaseId?: string; // UUID of the phase for accurate matching
    }>;
  };
  // Add release phases for phased releases
  releasePhases?:
    | Array<{
        id: string;
        phaseName: string;
        status: string;
        generalInfo?: any;
        primaryContacts?: any;
      }>
    | Record<string, any>;
  /** In Must Wins view, unique key for this card instance (row + quarter + issue). When set, manual expand state is tracked per instance so the same card in different rows expands independently. */
  mustWinInstanceKey?: string;
}

const TimelineCard: React.FC<TimelineCardProps> = ({
  releasePhaseId,
  projectType,
  title,
  categories,
  products,
  labels = [],
  releasePhase = '', // Change from labels to releasePhase
  riskLevel,
  lastUpdated,
  reportingSummary,
  childRelationships = [],
  features = [], // Use features array
  isQuarterExpanded = false,
  status = '',
  type = '',
  isCompact = false,
  dates,
  relationships = [],
  issueId = '',
  links = [],
  priority = '',
  scheduleRange,
  dateRange,
  relationship,
  releasePhases,
  showOnTimeline = false, // Default to false to use existing behavior
  wholeCard,
  mustWinInstanceKey
}) => {
  // Extract showOnTimeline from wholeCard if available, otherwise use the prop
  const actualShowOnTimeline = wholeCard?.showOnTimeline ?? showOnTimeline;
  // Extract showOnRoadmap from wholeCard
  const showOnRoadmap = wholeCard?.showOnRoadmap ?? false;
  // Extract secondaryDateStatus from wholeCard
  const secondaryDateStatus = wholeCard?.secondaryDateStatus;
  // Extract rsdLink from wholeCard if available
  const rsdLink = wholeCard?.rsdLink;
  // Extract revenueImpact from wholeCard
  // Check for boolean value (true) - the API sends boolean, not string
  const revenueImpact = React.useMemo(() => {
    // First check root level (for roadmap cards - Key Dates, Events, etc.)
    if (wholeCard?.revenueImpact === true) {
      return true;
    }

    // For non-phased releases with generalInfo structure
    if (wholeCard?.generalInfo?.revenueImpact === true) {
      return true;
    }

    // For phased releases, check if the current phase has revenueImpact enabled
    if (wholeCard?.releasePhases && releasePhaseId) {
      const currentPhase = Array.isArray(wholeCard.releasePhases)
        ? wholeCard.releasePhases.find(
            (phase: any) => phase.id === releasePhaseId
          )
        : null;
      return currentPhase?.generalInfo?.revenueImpact === true;
    }
    // Also check by phase name if releasePhaseId is not available
    if (wholeCard?.releasePhases && releasePhase) {
      const currentPhase = Array.isArray(wholeCard.releasePhases)
        ? wholeCard.releasePhases.find(
            (phase: any) => phase.phaseName === releasePhase
          )
        : null;
      return currentPhase?.generalInfo?.revenueImpact === true;
    }
    // Default to false for backward compatibility (when field doesn't exist)
    return false;
  }, [wholeCard, releasePhaseId, releasePhase]);
  const dispatch = useDispatch();
  const { actions } = useHomeSlice();
  const { isAdmin, canEditEvents, canDeleteEvents } =
    useEventKeyDatePermissions();

  // Get up to 3 product icons
  const productIconsArray = getProductIcons(products);

  // Three dots menu functionality for Events (admin only)

  const [cardExpanded, setCardExpanded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const {
    isSlideoutOpen: isSlideOutOpen,
    openSlideout,
    closeSlideout,
    currentIssueId
  } = useSlideoutUrl();
  const isCollapsed = useSelector(selectCollapseAll);
  const isUnNested = useSelector(selectUnNestAll);
  const unnestParentIds = useSelector(selectUnnestParentIds);
  const isSmall = useSelector(selectCollapseAll);
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltipTimer = useRef<NodeJS.Timeout | null>(null);
  const highlightedIssue = useSelector(selectHighlightedIssue);
  // Check composite keys: both UUID-based and phase-name-based for event highlighting
  const compositeKeyWithId = releasePhaseId
    ? `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`
    : null;
  const compositeKeyWithName = releasePhase
    ? `${issueId}-${releasePhase}-${actualShowOnTimeline}`
    : null;
  const isHighlighted =
    !!highlightedIssue[issueId] ||
    (compositeKeyWithId && !!highlightedIssue[compositeKeyWithId]) ||
    (compositeKeyWithName && !!highlightedIssue[compositeKeyWithName]);
  const docs = links.filter(link => link.type === 'DOCUMENTATION');
  const [tooltipPosition, setTooltipPosition] = useState<'top' | 'bottom'>(
    'bottom'
  );
  const cardRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const homeState = useSelector(selectHome);
  const showParkingLot = useSelector(selectParkingLot);
  const timelineIconsVisible = useSelector(selectTimelineIconsVisible);

  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  // Notification state for mark as complete actions
  const [notification, setNotification] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
  } | null>(null);

  // ✅ NEW: Temporary highlight state for tooltip updates
  const [isTemporarilyHighlighted, setIsTemporarilyHighlighted] =
    useState(false);
  const updateSuccessAlert = useSelector(selectUpdateSuccessAlert);

  // Feature drag-and-drop: local order state, synced from props
  const reorderDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const draggedProductIdRef = useRef<string | null>(null);
  const [orderedFeatures, setOrderedFeatures] = useState<any[]>(features || []);

  useEffect(() => {
    // Skip if a reorder is pending — don't clobber the user's drag order
    // before the debounced API call has fired
    if (reorderDebounceRef.current) return;
    setOrderedFeatures(features || []);
  }, [features]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (reorderDebounceRef.current) clearTimeout(reorderDebounceRef.current);
    };
  }, []);

  const canEditFeatures = (wholeCard?.canEdit ?? false) || isAdmin;
  // Drag-and-drop reorder is only meaningful for release types that have features.
  // Marketing Moments, Price Changes and Offers, etc. do not support feature reordering.
  const REORDERABLE_MOMENT_TYPES: string[] = [
    // MOMENT_TYPES.PRODUCT_RELEASE,
    // MOMENT_TYPES.MARKETING_MOMENT_AND_PRODUCT_RELEASE,
    // MOMENT_TYPES.NEW_1_0_RELEASE,
    MOMENT_TYPES.MARKETING_MOMENT
  ];
  const canReorderFeatures =
    canEditFeatures && !REORDERABLE_MOMENT_TYPES.includes(projectType);
  const [lastReorderedFeatureId, setLastReorderedFeatureId] = useState<
    string | null
  >(null);

  const handleFeaturesReorder = useCallback(
    (newOrder: any[], movedItemId?: string) => {
      if (movedItemId) {
        setLastReorderedFeatureId(movedItemId);
        setTimeout(() => setLastReorderedFeatureId(null), 1200);
      }

      // Derive which product was reordered from the moved item
      const movedFeature = newOrder.find(
        (f: any) => (f.featureId || f.issueId) === movedItemId
      );
      const affectedProductId = movedFeature?.productId ?? null;

      // Debounce the API call
      if (reorderDebounceRef.current) clearTimeout(reorderDebounceRef.current);
      reorderDebounceRef.current = setTimeout(async () => {
        reorderDebounceRef.current = null;
        if (!issueId) return;
        // Only send features belonging to the reordered product, with per-product display orders
        const productFeatures = affectedProductId
          ? newOrder.filter((f: any) => f.productId === affectedProductId)
          : newOrder;
        const body = {
          features: productFeatures.map((f: any, i: number) => ({
            featureId: (f.featureId || f.issueId) as string,
            displayOrder: i + 1
          }))
        };
        const res = await reorderReleaseFeatures(issueId, body);
        if (!res.success) {
          ToastQueue.negative('Unable to save feature order.', {
            timeout: 5000
          });
          // Revert to server order
          setOrderedFeatures(features || []);
        }
      }, REORDER_DEBOUNCE_MS);
    },
    [issueId, features]
  );

  const featureReorder = useReorderable(
    orderedFeatures,
    setOrderedFeatures,
    (f: any) => (f.featureId || f.issueId) as string,
    {
      disabled: !DRAG_DROP_REORDER_ENABLED || !canReorderFeatures,
      rowDataTestIdPrefix: 'timeline-card-feature-wrapper-',
      dragImageOffsetX: 16,
      dragImageOffsetY: 20,
      onPersist: handleFeaturesReorder
    }
  );

  // Derive the product being dragged from existing reorder state — drives the denied-target UI
  const draggedProduct = featureReorder.draggedId
    ? orderedFeatures.find(
        (f: any) => (f.featureId || f.issueId) === featureReorder.draggedId
      )?.productId ?? null
    : null;

  // ✅ Watch for successful updates and highlight the card temporarily.
  // In timeline view the same issueId can appear in multiple rows (audience/strategy);
  // when the alert is cleared, ALL matching cards must clear highlight reactively.
  useEffect(() => {
    if (!updateSuccessAlert) {
      setIsTemporarilyHighlighted(false);
      return;
    }

    const alertIssueId = updateSuccessAlert.issueId;
    const alertPhaseId = updateSuccessAlert.releasePhaseId || null;

    const isMatch =
      alertIssueId === issueId &&
      (releasePhaseId
        ? alertPhaseId === releasePhaseId
        : alertPhaseId === null || alertPhaseId === '');

    if (isMatch) {
      setIsTemporarilyHighlighted(true);

      // One timer per card; first to fire clears the alert so all cards clear via effect above
      const clearTimer = setTimeout(() => {
        dispatch(actions.clearUpdateSuccessAlert());
      }, 3000);

      return () => clearTimeout(clearTimer);
    }
  }, [updateSuccessAlert, issueId, releasePhaseId, dispatch, actions]);

  // State to track if date container was clicked
  const [isDateClicked, setIsDateClicked] = useState(false);
  const [tooltipPlacement, setTooltipPlacement] = useState<{
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  }>({ vertical: 'top', horizontal: 'left' });
  const dateContainerRef = useRef<HTMLDivElement>(null);
  const dateTooltipRef = useRef<HTMLDivElement>(null);

  // Handle clicks outside the date container and tooltip
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isDateClicked &&
        dateContainerRef.current &&
        dateTooltipRef.current &&
        !dateContainerRef.current.contains(event.target as Node) &&
        !dateTooltipRef.current.contains(event.target as Node)
      ) {
        setIsDateClicked(false);
        dispatch(actions.resetTooltipDetails());
      }
    };

    if (isDateClicked) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDateClicked, dispatch, actions]);

  const handleTooltipMouseEnter = (key: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(key);
    }, 1000);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  const isExpandable =
    (childRelationships && childRelationships.length > 0) ||
    (features && features.length > 0);

  // Helper function to check if any GA or Public Beta phase is completed
  const hasCompletedGAOrPublicBeta = () => {
    if (!releasePhases) return false;

    // Handle both array and object formats
    const phasesArray = Array.isArray(releasePhases)
      ? releasePhases
      : Object.keys(releasePhases).map(key => ({
          phaseName: key,
          ...releasePhases[key]
        }));

    return phasesArray.some(phase => {
      const phaseName = phase.phaseName?.toLowerCase() || '';
      const phaseStatus = phase.status;

      return (
        (phaseName === 'ga' || phaseName === 'public beta') &&
        phaseStatus === STATUS_VALUES.COMPLETE
      );
    });
  };

  // Get effective status and normalize it
  const effectiveStatus = getEffectiveStatus(
    status,
    showOnRoadmap,
    actualShowOnTimeline,
    secondaryDateStatus
  );
  const normalizedEffectiveStatus = normalizeStatus(effectiveStatus);

  const isClosed =
    normalizedEffectiveStatus === STATUS_VALUES.COMPLETE ||
    normalizedEffectiveStatus === STATUS_VALUES.CLOSED ||
    hasCompletedGAOrPublicBeta();

  // Helper function to determine if this should have MMPR styling
  const shouldApplyMmprStyling = () => {
    // First check the projectType prop - ONLY for "Product Release without Marketing"
    if (
      projectType === MOMENT_TYPES.PRODUCT_RELEASE ||
      projectType === 'Product Release without Marketing'
    ) {
      return true;
    }

    // If projectType is undefined/null, check the phases for momentType
    if (!projectType && releasePhases) {
      const phasesArray = Array.isArray(releasePhases)
        ? releasePhases
        : Object.keys(releasePhases).map(key => ({
            phaseName: key,
            ...releasePhases[key]
          }));

      // Check if any phase has the "Product Release without Marketing" momentType
      return phasesArray.some(phase => {
        const momentType = phase.generalInfo?.momentType;
        return (
          momentType === MOMENT_TYPES.PRODUCT_RELEASE ||
          momentType === 'Product Release without Marketing'
        );
      });
    }

    // FALLBACK: Only apply if we have explicit confirmation this is MMPR
    // Remove broad heuristic that was causing false positives

    return false;
  };

  const isEventType = type === 'Events' || type.includes('Events');
  const isKeyDateType = type === 'Key Dates' || type.includes('Key Date');
  const showInfoIcons = timelineIconsVisible && !isEventType && !isKeyDateType;
  const isEolCard = isEolMoment(projectType);

  const titleParts = getRtmPhasedTitleParts({
    momentType: projectType,
    rawTitle: title,
    releasePhase,
    releasePhaseId,
    releasePhases: Array.isArray(wholeCard?.releasePhases)
      ? wholeCard!.releasePhases
      : null
  });
  const storageTitleForCard = titleParts.storageTitle;
  const displayTitle = titleParts.displayTitle;
  const eolTooltipTitle = isEolCard
    ? formatEolPhaseDisplayTitle(releasePhase, title)
    : displayTitle;

  const displayDates = getTimelineDisplayDates({
    dates,
    projectType,
    showOnTimeline: actualShowOnTimeline,
    pco: wholeCard?.pco === true
  });

  const isOverdue = (() => {
    if (isClosed) return false;

    // ⭐ IMPORTANT: Use displayDates which respects showOnTimeline flag
    // When showOnTimeline is true, we're displaying releaseDate/effectiveDate
    // so the overdue logic should check those dates, not targetStart/targetEnd

    // Priority 1: If displayDates.end exists, use it (regardless of start)
    // This ensures that for date ranges, we only show overdue after the end date
    if (displayDates.end) {
      return moment(displayDates.end).isBefore(moment(), 'day');
    }

    // Priority 2: If no end but start exists, use start
    if (displayDates.start) {
      return moment(displayDates.start).isBefore(moment(), 'day');
    }

    // Priority 3: Check endMonth for Events that span across months
    // This handles cases like "Aug 2025" to "Sep 2025" - use the end month
    if (displayDates.endMonth) {
      // First try to parse if the month already contains the year (e.g., "Sep 2025")
      const monthWithYear = displayDates.endMonth.match(/(\w+)\s+(\d{4})/);
      if (monthWithYear) {
        const monthName = monthWithYear[1];
        const year = parseInt(monthWithYear[2]);
        const endMonthDate = getMonthEndDate(monthName, year);
        if (endMonthDate) {
          return moment(endMonthDate).isBefore(moment(), 'day');
        }
      } else if (displayDates.quarter) {
        // Fallback: Extract year from quarter for context
        const quarterMatch = displayDates.quarter.match(/Q\d\s+(\d{4})/);
        if (quarterMatch) {
          const year = parseInt(quarterMatch[1]);
          const endMonthDate = getMonthEndDate(displayDates.endMonth, year);
          if (endMonthDate) {
            return moment(endMonthDate).isBefore(moment(), 'day');
          }
        }
      }
    }

    // Priority 4: Check quarter (e.g., "Q3 2025")
    if (displayDates.quarter) {
      const quarterEndDate = getQuarterEndDate(displayDates.quarter);
      if (quarterEndDate) {
        return moment(quarterEndDate).isBefore(moment(), 'day');
      }
    }

    // Priority 5: Check month (e.g., "Aug 2025" or "Aug")
    if (displayDates.month) {
      // First try to parse if the month already contains the year (e.g., "Aug 2025")
      const monthWithYear = displayDates.month.match(/(\w+)\s+(\d{4})/);
      if (monthWithYear) {
        const monthName = monthWithYear[1];
        const year = parseInt(monthWithYear[2]);
        const monthEndDate = getMonthEndDate(monthName, year);
        if (monthEndDate) {
          return moment(monthEndDate).isBefore(moment(), 'day');
        }
      } else if (displayDates.quarter) {
        // Fallback: Only check month if quarter is also present to provide year context
        const quarterMatch = displayDates.quarter.match(/Q\d\s+(\d{4})/);
        if (quarterMatch) {
          const year = parseInt(quarterMatch[1]);
          const monthEndDate = getMonthEndDate(displayDates.month, year);
          if (monthEndDate) {
            return moment(monthEndDate).isBefore(moment(), 'day');
          }
        }
      }
    }

    return false;
  })();

  const shouldBeCompact = isCompact || isEventType || isSmall;

  // Get all releases data from homeState
  const allReleases = homeState?.releaseData?.releaseScheduled || [];

  const openSlideOut = () => {
    setIsDateClicked(false); //Close Quick edit tooltip before opening slideout
    // If rsdLink is present and not null, open it in a new window
    if (rsdLink && rsdLink.trim()) {
      window.open(rsdLink, '_blank', 'noopener,noreferrer');
    } else {
      // Otherwise, open the slideout with releasePhaseId if available
      openSlideout(issueId, undefined, releasePhaseId);
    }
  };

  // Calculate implementedCount from relationship badgecount
  const implementedCount = relationship?.badgecount
    ? parseInt(relationship.badgecount, 10)
    : 0;

  const highLightCards = () => {
    if (type.includes('Events')) {
      if (!isHighlighted) {
        // Clear existing highlights first, then add new ones
        dispatch(actions.clearHighlightedIssues());

        // Add highlighting with composite keys
        let parentToUnnest: Set<string> = new Set();
        let highlightSet: Set<string> = new Set();

        // Use releaseHierarchyPaths from relationship object for composite keys
        if (
          relationship?.releaseHierarchyPaths &&
          relationship.releaseHierarchyPaths.length > 0
        ) {
          // Extract issue IDs and parent IDs from releaseHierarchyPaths
          relationship.releaseHierarchyPaths.forEach(path => {
            if (path.issue) {
              // ⭐ Use composite keys matching the isHighlighted check
              if (path.phaseId) {
                const compositeKey = `${path.issue}-${path.phaseId}-${actualShowOnTimeline}`;
                highlightSet.add(compositeKey);
              } else if (path.phase) {
                const compositeKey = `${path.issue}-${path.phase}-${actualShowOnTimeline}`;
                highlightSet.add(compositeKey);
              } else {
                highlightSet.add(path.issue);
              }

              // If this is a child, unnest its parent
              if (path.parent && path.parent !== issueId) {
                parentToUnnest.add(path.parent);
                highlightSet.add(path.parent);
              }
            }
          });
        }

        // Always add the event itself with composite key if it has phase info
        if (releasePhaseId) {
          const eventCompositeKey = `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`;
          highlightSet.add(eventCompositeKey);
        } else if (releasePhase) {
          const eventCompositeKey = `${issueId}-${releasePhase}-${actualShowOnTimeline}`;
          highlightSet.add(eventCompositeKey);
        } else {
          highlightSet.add(issueId);
        }

        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: Array.from(highlightSet),
            source: 'single-click' // ✅ Mark as single-click highlight
          })
        );
      } else {
        // If already highlighted, clear ALL highlighting
        dispatch(actions.clearHighlightedIssues());
      }
    }
  };

  // Add effect to collapse cards in compact mode
  useEffect(() => {
    if (isCompact) {
      setCardExpanded(false);
    }
  }, [isCompact, isExpandable]);

  // Add effect to expand cards in un-nested mode
  useEffect(() => {
    if (isExpandable) {
      // ✅ Check sessionStorage for manually expanded cards
      const expandedCards = JSON.parse(
        sessionStorage.getItem('manually_expanded_cards') || '[]'
      );
      const cardCompositeKey = releasePhaseId
        ? `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`
        : issueId;
      // In Must Wins view, use per-instance key so the same card in different rows expands independently
      const expandKey = mustWinInstanceKey ?? cardCompositeKey;

      const isManuallyExpanded = expandedCards.includes(expandKey);

      if (isManuallyExpanded) {
        // Card was manually expanded, keep it expanded
        setCardExpanded(true);
      } else {
        // ✅ FIX: Check if unnestParentIds is empty (global toggle) or has specific IDs (filter)
        // - If unnestParentIds is empty → Global toggle: unnest ALL cards when isUnNested = true
        // - If unnestParentIds has values → Filter mode: only unnest cards in the array
        const isFilterMode = unnestParentIds.length > 0;

        if (isFilterMode) {
          // Filter mode: check if this card is in the unnest list
          const shouldExpand =
            unnestParentIds.includes(cardCompositeKey) ||
            unnestParentIds.includes(issueId);

          // ✅ FIX: In filter mode (search/events), expand if card is in the list
          // Don't require isUnNested to be true (it's for global toggle only)
          setCardExpanded(shouldExpand);
        } else {
          // Global mode: follow isUnNested flag directly
          setCardExpanded(isUnNested);
        }
      }
    }
  }, [
    isUnNested,
    isExpandable,
    unnestParentIds,
    issueId,
    releasePhaseId,
    actualShowOnTimeline,
    mustWinInstanceKey
  ]);

  // ❌ REMOVED: Separate useEffect for unnestParentIds - now handled above
  // This was causing conflicting logic between global and filter-based unnesting

  // Add effect to handle collapse all
  useEffect(() => {
    if (isSmall) {
      setCardExpanded(false);
    }
  }, [isSmall]);

  // Add effect to handle un-nesting in collapsed mode
  useEffect(() => {
    if (isSmall && isUnNested && isExpandable) {
      setCardExpanded(true);
    }
  }, [isSmall, isUnNested, isExpandable]);

  const formatDate = (dateString: string) => {
    const day = moment(dateString).format('D');
    const month = moment(dateString).format('MMM').toUpperCase();
    return { day, month };
  };

  const renderEventDate = () => {
    // Case 1: Has both start and end dates
    if (displayDates.start && displayDates.end) {
      const start = formatDate(displayDates.start);
      const end = formatDate(displayDates.end);

      // Check if start and end dates are exactly the same
      if (displayDates.start === displayDates.end) {
        return (
          <>
            <Text
              UNSAFE_className={styles.day}
            >{`${start.day} ${start.month}`}</Text>
          </>
        );
      }

      if (start.month === end.month) {
        return (
          <>
            <Text
              UNSAFE_className={styles.day}
            >{`${start.day}-${end.day}`}</Text>
            <Text UNSAFE_className={styles.month}>{start.month}</Text>
          </>
        );
      }

      return (
        <>
          <Text
            UNSAFE_className={styles.day}
          >{`${start.day} ${start.month} - ${end.day} ${end.month}`}</Text>
        </>
      );
    }

    // Case 1.5: Has only start date
    if (displayDates.start) {
      const { day, month } = formatDate(displayDates.start);
      return (
        <>
          <Text UNSAFE_className={styles.day}>{`${day} ${month}`}</Text>
        </>
      );
    }

    // Case 2: Has target month
    if (displayDates.month) {
      // Extract just the month from "Jul 2025" format
      const month = displayDates.month.split(' ')[0];
      if (displayDates.endMonth) {
        // Handle month range case
        const endMonth = displayDates.endMonth.split(' ')[0];
        return (
          <>
            <Text
              UNSAFE_className={styles.month}
            >{`${month} - ${endMonth}`}</Text>
          </>
        );
      }
      return (
        <>
          <Text UNSAFE_className={styles.month}>{month}</Text>
        </>
      );
    }

    // Case 3: Has target quarter
    if (displayDates.quarter) {
      // Extract just the quarter from "Q3 2025" format
      const quarter = displayDates.quarter.split(' ')[0];
      return (
        <>
          <Text UNSAFE_className={styles.month}>{quarter}</Text>
        </>
      );
    }

    // Case 4: Fallback to dateStatus (TBD or N/A) when no dates are available
    if (displayDates.dateStatus) {
      return (
        <>
          <Text UNSAFE_className={styles.month}>{displayDates.dateStatus}</Text>
        </>
      );
    }

    return null;
  };

  const renderDate = () => {
    if (isEventType) {
      return renderEventDate();
    }

    // Priority 1: start date (targetStart or releaseStart/effectiveDate)
    if (displayDates.start) {
      const { day, month } = formatDate(displayDates.start);
      if (isCompact || isSmall) {
        return (
          <>
            <Text UNSAFE_className={styles.day}>
              {day} {month}
            </Text>
          </>
        );
      }
      return (
        <>
          <Text UNSAFE_className={styles.day}>{day}</Text>
          <Text UNSAFE_className={styles.month}>{month}</Text>
        </>
      );
    }

    // Priority 2: month (targetMonth or releaseMonth)
    if (displayDates.month) {
      // Extract just the month from "Jul 2025" format
      const month = displayDates.month.split(' ')[0];
      return (
        <>
          <Text UNSAFE_className={styles.month}>{month}</Text>
        </>
      );
    }

    // Priority 3: quarter (targetQuarter or releaseQuarter)
    if (displayDates.quarter) {
      // Extract just the quarter from "Q3 2025" format
      const quarter = displayDates.quarter.split(' ')[0];
      return (
        <>
          <Text UNSAFE_className={styles.month}>{quarter}</Text>
        </>
      );
    }

    // Priority 4: Fallback to dateStatus (TBD or N/A) when no dates are available
    if (displayDates.dateStatus) {
      return (
        <>
          <Text UNSAFE_className={styles.month}>{displayDates.dateStatus}</Text>
        </>
      );
    }

    return null;
  };

  const getStatusIcon = () => {
    const size = shouldBeCompact ? 22 : 24;
    const normalizedRiskLevel = normalizeRiskLevelToStored(riskLevel);
    if (normalizedRiskLevel === RISK_LEVELS.LOW) {
      return <LegendRiskLowIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.MODERATE) {
      return <LegendRiskModerateIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.HIGH) {
      return <LegendRiskHighIcon width={size} height={size} />;
    } else if (normalizedRiskLevel === RISK_LEVELS.IN_PLANNING) {
      return <LegendInPlanningIcon width={18} height={18} />;
    }
    return null;
  };

  // Helper to get display name for risk level
  const getRiskLevelDisplayName = (risk?: string): string => {
    if (!risk) return '';
    const normalized = risk.toLowerCase();
    if (normalized === 'green' || normalized === 'low') return 'Low';
    if (normalized === 'yellow' || normalized === 'moderate') return 'Moderate';
    if (normalized === 'red' || normalized === 'high') return 'High';
    if (normalized === 'in planning' || normalized === 'in_planning')
      return 'In Planning';
    return risk; // fallback to original value
  };

  useEffect(() => {
    return () => {
      if (tooltipTimer.current) {
        clearTimeout(tooltipTimer.current);
      }
    };
  }, []);

  /**
   * Calculate vertical position (top or bottom) based on available space in container
   */
  const calculateVerticalPosition = (
    elementRect: DOMRect,
    containerRect: DOMRect,
    safetyMargin: number = 20
  ): 'top' | 'bottom' => {
    // Calculate space relative to the CONTAINER, not viewport
    const spaceBelow = containerRect.bottom - elementRect.bottom - safetyMargin;
    const spaceAbove = elementRect.top - containerRect.top - safetyMargin;

    // Position where there's more space
    return spaceAbove > spaceBelow ? 'top' : 'bottom';
  };

  /**
   * Calculate horizontal position (left, center, or right) based on available space and overflow
   */
  const calculateHorizontalPosition = (
    elementRect: DOMRect,
    tooltipWidth: number,
    availableWidth: number,
    safetyMargin: number = 20
  ): 'left' | 'center' | 'right' => {
    // Calculate if tooltip would overflow on the right side when positioned at left: 0
    const wouldOverflowRight = elementRect.left + tooltipWidth > availableWidth;

    // Calculate if tooltip would overflow on the left side when positioned at right: 0
    const wouldOverflowLeft = elementRect.right - tooltipWidth < safetyMargin;

    if (wouldOverflowRight && !wouldOverflowLeft) {
      // If it would overflow right but not left, align to right edge
      return 'right';
    } else if (wouldOverflowRight && wouldOverflowLeft) {
      // If it would overflow both sides, try to center it
      const centerPosition =
        elementRect.left + elementRect.width / 2 - tooltipWidth / 2;
      if (
        centerPosition >= safetyMargin &&
        centerPosition + tooltipWidth <= availableWidth
      ) {
        return 'center';
      } else {
        // If centering still doesn't work, choose the side with more space
        const spaceLeft = elementRect.left - safetyMargin;
        const spaceRight = availableWidth - elementRect.right;
        return spaceLeft > spaceRight ? 'right' : 'left';
      }
    } else {
      // Default to left alignment if no overflow issues
      return 'left';
    }
  };

  /**
   * Calculate tooltip position (vertical and horizontal)
   */
  const calculateTooltipPosition = (
    element: HTMLDivElement | null,
    tooltipHeight: number,
    tooltipWidth: number = 400
  ): {
    vertical: 'top' | 'bottom';
    horizontal: 'left' | 'center' | 'right';
  } => {
    if (!element)
      return {
        vertical: 'top',
        horizontal: 'left'
      };

    const rect = element.getBoundingClientRect();

    // Get the scrollable container (roadmap-main-grid)
    const container = document.querySelector(
      '[data-testid="roadmap-main-grid"]'
    );
    const containerRect = container?.getBoundingClientRect();

    if (!containerRect) {
      // Fallback to viewport if container not found
      return {
        vertical: 'bottom',
        horizontal: 'left'
      };
    }

    // Add safety margins to account for scrollbars and UI elements
    const safetyMargin = 20;
    // Account for parking lot when open (grid track width; border lives inside the track)
    const parkingLotWidth = showParkingLot ? PARKING_LOT_GRID_COLUMN_PX : 0;
    const availableWidth = window.innerWidth - safetyMargin - parkingLotWidth;

    // Calculate positions using dedicated functions
    const vertical = calculateVerticalPosition(
      rect,
      containerRect,
      safetyMargin
    );
    const horizontal = calculateHorizontalPosition(
      rect,
      tooltipWidth,
      availableWidth,
      safetyMargin
    );

    return { vertical, horizontal };
  };

  const handleDoubleClick = e => {
    e.preventDefault(); // Prevent any default double-click behavior
    if (isEventType) {
      // Check if this event is already selected (in the filter)
      const isAlreadySelected =
        homeState.events?.selectedEvents?.includes(issueId);

      if (isAlreadySelected) {
        // Deselect this event and clear all highlights
        dispatch(actions.setSelectedEvents([]));
        // ✅ Use clearHighlightedIssues to completely clear highlights (not merge)
        dispatch(actions.clearHighlightedIssues());
        // Clear the sticky session (no need to reload data - state update will handle UI refresh)
        saveUserPreferences({ events: [] });
      } else {
        // ⭐ CLEAR ALL OTHER FILTERS when double-clicking on an event (same as single-click from filter dropdown)
        dispatch(actions.clearGenAiFilters());
        dispatch(actions.setSelectedFeatures([]));
        dispatch(actions.setSelectedProducts([]));
        dispatch(actions.setSelectedCats([]));
        dispatch(actions.setSelectedGroups([]));
        dispatch(actions.setSelectedAudiences([]));
        dispatch(actions.clearCategoryProductSelection());

        // Clear saved preferences for all filters
        saveUserPreferences({ genAiFilters: [] });
        saveUserPreferences({ features: [] });
        saveUserPreferences({ products: [] });
        saveUserPreferences({ categories: [] });
        saveUserPreferences({ productGroups: [] });
        saveUserPreferences({ productGroupLabels: [] });
        saveUserPreferences({ audiences: [] });
        saveUserPreferences({ audienceLabels: [] });

        // Use releaseHierarchyPaths from relationship object
        let parentToUnnest: Set<string> = new Set();
        let highlightSet: Set<string> = new Set();

        if (
          relationship?.releaseHierarchyPaths &&
          relationship.releaseHierarchyPaths.length > 0
        ) {
          // Extract issue IDs and parent IDs from releaseHierarchyPaths
          relationship.releaseHierarchyPaths.forEach(path => {
            if (path.issue) {
              // Prefer phaseId (UUID) over phase name for more accurate matching
              if (path.phaseId) {
                // Create composite key with UUID: issueId-phaseId-showOnTimeline
                const compositeKey = `${path.issue}-${path.phaseId}-false`;
                highlightSet.add(compositeKey);
              } else if (path.phase) {
                // Fallback to phase name if phaseId not available
                const compositeKey = `${path.issue}-${path.phase}-false`;
                highlightSet.add(compositeKey);
              } else {
                // No phase specified, highlight all phases/instances of this issue
                highlightSet.add(path.issue);
              }

              // If this is a child, track parent (for potential future use)
              if (path.parent && path.parent !== issueId) {
                parentToUnnest.add(path.parent);
                highlightSet.add(path.parent);
              }
            }
          });
        }
        highlightSet.add(issueId); // always highlight the event itself
        // Set the selected event in the filter (this triggers filtering in Roadmap.tsx)
        dispatch(actions.setSelectedEvents([issueId]));
        dispatch(
          actions.updateHighlightedIssue({
            relatedIssues: Array.from(highlightSet),
            source: 'filter' // ✅ Mark as filter highlight (double-click adds to filter)
          })
        );

        // Check if any highlighted items match items in the TBD section
        // If yes, open the parking lot to show them
        const tbdData = homeState.releaseData?.tbd || [];

        const hasMatchInTBD = tbdData.some((item: any) =>
          item.project?.some((project: any) => {
            if (!project.issueId) return false;

            // Check for direct match (issueId only)
            if (highlightSet.has(project.issueId)) return true;

            // Check for composite key match with releasePhaseId (UUID)
            if (project.releasePhaseId) {
              const compositeKey = `${project.issueId}-${project.releasePhaseId}-false`;
              if (highlightSet.has(compositeKey)) return true;
            }

            // Check for composite key match with releasePhase (string name)
            if (project.releasePhase) {
              const compositeKey = `${project.issueId}-${project.releasePhase}-false`;
              if (highlightSet.has(compositeKey)) return true;
            }

            return false;
          })
        );

        // If there are matches in TBD and parking lot is hidden, open it
        if (hasMatchInTBD) {
          dispatch(actions.setParkingLot(true));
        }

        saveUserPreferences({ events: [issueId] });
      }
    }
  };

  const renderTooltip = () => {
    if (isEventType || !showTooltip) return null;
    return (
      <div
        className={`${styles.tooltip} ${styles[tooltipPosition]} ${
          showTooltip ? styles.visible : ''
        }`}
      >
        <div className={styles.tooltipContent}>
          <div className={styles.tooltipRow}>
            <div className={styles.tooltipText}>{displayTitle}</div>
          </div>
        </div>
      </div>
    );
  };

  const renderDateTooltip = () => {
    if (isEventType) return null;

    return (
      <DateTooltip
        isDateClicked={isDateClicked}
        setIsDateClicked={setIsDateClicked}
        dateTooltipRef={dateTooltipRef}
        categories={categories}
        products={products}
        title={displayTitle}
        storageTitle={storageTitleForCard}
        phaseTitleLabel={titleParts.phaseLabel || undefined}
        isClosed={isClosed}
        riskLevel={riskLevel}
        issueId={issueId}
        getStatusIcon={getStatusIcon}
        handleTooltipMouseEnter={handleTooltipMouseEnter}
        handleTooltipMouseLeave={handleTooltipMouseLeave}
        lastUpdated={lastUpdated}
        reportingSummary={reportingSummary}
        handleImageError={handleImageError}
        wholeCard={wholeCard}
        tooltipPlacement={tooltipPlacement}
        isOverdue={isOverdue} // ✅ Pass isOverdue to show dates in red
      />
    );
  };

  const handleImageError = event => {
    event.target.src = AdobeIcon;
  };

  const renderCompactContent = () => {
    return (
      <>
        <div className={styles.headingContainer}>
          {isEventType ? (
            <TooltipTrigger
              isOpen={openTooltip === 'title'}
              onOpenChange={isOpen => {
                if (!isOpen) setOpenTooltip(null);
              }}
              placement="top"
            >
              <div
                className={styles.compactTitleWrapper}
                onMouseEnter={() => handleTooltipMouseEnter('title')}
                onMouseLeave={handleTooltipMouseLeave}
              >
                <ActionButton
                  data-testid={`timeline-card-event-title-${issueId}`}
                  UNSAFE_className={`${styles.heading} ${styles.eventTitle}`}
                  onPress={highLightCards}
                  UNSAFE_style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none'
                  }}
                >
                  <CardTitleDisplay
                    projectType={projectType}
                    releasePhase={releasePhase}
                    title={title}
                    displayTitle={displayTitle}
                    className={styles.headingText}
                  />
                </ActionButton>
              </div>
              <Tooltip>{eolTooltipTitle}</Tooltip>
            </TooltipTrigger>
          ) : (
            <TooltipTrigger
              isOpen={openTooltip === 'title'}
              onOpenChange={isOpen => {
                if (!isOpen) setOpenTooltip(null);
              }}
              placement="top"
            >
              <div
                className={styles.compactTitleWrapper}
                onMouseEnter={() => handleTooltipMouseEnter('title')}
                onMouseLeave={handleTooltipMouseLeave}
              >
                <ActionButton
                  data-testid={`timeline-card-title-${issueId}`}
                  UNSAFE_className={styles.heading}
                  onPress={openSlideOut}
                  isQuiet
                  UNSAFE_style={{
                    border: 'none',
                    backgroundColor: 'transparent',
                    outline: 'none',
                    height: 'fit-content'
                  }}
                >
                  <CardTitleDisplay
                    projectType={projectType}
                    releasePhase={releasePhase}
                    title={title}
                    displayTitle={displayTitle}
                    className={styles.headingText}
                  />
                </ActionButton>
              </div>
              <Tooltip>{eolTooltipTitle}</Tooltip>
            </TooltipTrigger>
          )}
        </div>
        {!isEventType && (showInfoIcons || isExpandable) && (
          <div
            className={styles.compactIcons}
            data-testid={`timeline-card-compact-icons-${issueId}`}
          >
            {/* 1. Revenue Impact (Must Win $) - leftmost */}
            {showInfoIcons && (
              <RevenueImpactIcon
                issueId={issueId}
                revenueImpact={revenueImpact}
                openTooltip={openTooltip}
                setOpenTooltip={setOpenTooltip}
                handleTooltipMouseEnter={handleTooltipMouseEnter}
                handleTooltipMouseLeave={handleTooltipMouseLeave}
                iconActionButtonClass={styles.iconActionButton}
                tooltipWrapperClass={styles.tooltipWrapper}
                isCompact={true}
              />
            )}

            {/* 2. Expand/unnest icon */}
            {isExpandable && (
              <TooltipTrigger
                isOpen={openTooltip === 'expand'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  className={styles.tooltipWrapper}
                  onMouseEnter={() => handleTooltipMouseEnter('expand')}
                  onMouseLeave={handleTooltipMouseLeave}
                >
                  <ActionButton
                    data-testid={`timeline-card-compact-expand-button-${issueId}`}
                    isQuiet
                    UNSAFE_className={styles.iconActionButton}
                    aria-label="View Nested Moments"
                    onPress={() => {
                      const newExpandedState = !cardExpanded;
                      setCardExpanded(newExpandedState);

                      // ✅ Save manually expanded cards to sessionStorage (per-instance key in Must Wins view)
                      const cardCompositeKey = releasePhaseId
                        ? `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`
                        : issueId;
                      const expandKey = mustWinInstanceKey ?? cardCompositeKey;

                      const expandedCards = JSON.parse(
                        sessionStorage.getItem('manually_expanded_cards') ||
                          '[]'
                      );

                      if (newExpandedState) {
                        // Add to expanded list
                        if (!expandedCards.includes(expandKey)) {
                          expandedCards.push(expandKey);
                          sessionStorage.setItem(
                            'manually_expanded_cards',
                            JSON.stringify(expandedCards)
                          );
                        }
                      } else {
                        // Remove from expanded list
                        const filtered = expandedCards.filter(
                          id => id !== expandKey
                        );
                        sessionStorage.setItem(
                          'manually_expanded_cards',
                          JSON.stringify(filtered)
                        );
                      }

                      // If in compact mode, disable it but don't restore all quarters
                      // Only focus on the current card's quarter to maintain user focus
                      if (isCompact) {
                        // Disable compact mode and update localStorage using helper
                        dispatch(actions.setCompact(false));
                        saveUserPreferences({ isCompact: false });

                        // Don't restore all quarters - let user manually expand them if needed
                        // This keeps focus on the current card they want to see nested content for
                      }
                    }}
                  >
                    <Image height="size-225" src={ExpandIcon} alt="" />
                  </ActionButton>
                </div>
                <Tooltip>{cardExpanded ? 'Collapse' : 'Expand'}</Tooltip>
              </TooltipTrigger>
            )}

            {/* 3. P/PB icons (phase badges) if present */}
            {showInfoIcons &&
              releasePhase &&
              getReleasePhaseIcon(releasePhase) && (
                <TooltipTrigger
                  isOpen={
                    openTooltip === `label-${getReleasePhaseIcon(releasePhase)}`
                  }
                  onOpenChange={isOpen => {
                    if (!isOpen) setOpenTooltip(null);
                  }}
                  placement="top"
                >
                  <div
                    className={styles.tooltipWrapper}
                    onMouseEnter={() =>
                      handleTooltipMouseEnter(
                        `label-${getReleasePhaseIcon(releasePhase)}`
                      )
                    }
                    onMouseLeave={handleTooltipMouseLeave}
                  >
                    <ActionButton
                      data-testid={`timeline-card-label-${getReleasePhaseIcon(
                        releasePhase
                      )}-${issueId}`}
                      isQuiet
                      UNSAFE_className={styles.iconActionButton}
                      aria-label={releasePhase}
                    >
                      {getReleasePhaseIcon(releasePhase) === 'beta' ? (
                        <LegendBetaMiniIcon />
                      ) : (
                        <LegendPrivateBetaMiniIcon />
                      )}
                    </ActionButton>
                  </div>
                  <Tooltip>{releasePhase}</Tooltip>
                </TooltipTrigger>
              )}

            {/* 4. Product Icon - shows first product with + indicator for multi-product */}
            {showInfoIcons && products && products.length > 0 && (
              <TooltipTrigger
                isOpen={openTooltip === 'product'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  onMouseEnter={() => handleTooltipMouseEnter('product')}
                  onMouseLeave={handleTooltipMouseLeave}
                >
                  <ActionButton
                    data-testid={`timeline-card-compact-product-icon-${issueId}`}
                    isQuiet
                    UNSAFE_className={styles.iconActionButton}
                    aria-label="Product"
                  >
                    <ProductIconCompact products={products} />
                  </ActionButton>
                </div>
                <Tooltip>
                  {Array.isArray(products)
                    ? products.length > 1
                      ? `${products[0]} +${products.length - 1} more`
                      : products[0]
                    : products}
                </Tooltip>
              </TooltipTrigger>
            )}

            {/* 5. Risk Level (status) - rightmost */}
            {showInfoIcons && !isClosed && riskLevel && riskLevel !== 'NA' && (
              <TooltipTrigger
                isOpen={openTooltip === 'risk'}
                onOpenChange={isOpen => {
                  if (!isOpen) setOpenTooltip(null);
                }}
                placement="top"
              >
                <div
                  className={styles.tooltipWrapper}
                  onMouseEnter={() => handleTooltipMouseEnter('risk')}
                  onMouseLeave={handleTooltipMouseLeave}
                >
                  <ActionButton
                    data-testid={`timeline-card-compact-risk-level-${issueId}`}
                    isQuiet
                    UNSAFE_className={styles.iconActionButton}
                    aria-label={`Risk Level: ${getRiskLevelDisplayName(
                      riskLevel
                    )}`}
                  >
                    {getStatusIcon()}
                  </ActionButton>
                </div>
                <Tooltip>
                  <RiskLevelTooltipContent
                    riskLevel={riskLevel}
                    className={styles.riskLevelText}
                  />
                </Tooltip>
              </TooltipTrigger>
            )}
          </div>
        )}
      </>
    );
  };

  // Add refs for parent and child cards
  const parentCardRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const childRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const featureContainerRef = useRef<HTMLDivElement>(null);
  const [connectorLines, setConnectorLines] = useState<
    Array<{
      path: string;
      isFeature: boolean;
      isLastChild: boolean;
    }>
  >([]);

  // Function to calculate connector lines positions
  const calculateConnectorLines = useCallback(() => {
    if (!parentCardRef.current || !containerRef.current || !cardExpanded)
      return;

    const containerRect = containerRef.current.getBoundingClientRect();
    const parentRect = parentCardRef.current.getBoundingClientRect();

    // Convert to relative coordinates
    const parentRelative = {
      left: parentRect.left - containerRect.left,
      right: parentRect.right - containerRect.left,
      top: parentRect.top - containerRect.top,
      bottom: parentRect.bottom - containerRect.top,
      width: parentRect.width,
      height: parentRect.height
    };

    const newLines: Array<{
      path: string;
      isFeature: boolean;
      isLastChild: boolean;
    }> = [];

    // Calculate lines for regular child cards only
    const regularChildren = childRelationships.filter(
      child => child.projectType !== MOMENT_TYPES.FEATURE
    );

    // First pass: Find the last child's main card position
    let lastChildY = 0;
    const lastChild = regularChildren[regularChildren.length - 1];
    if (lastChild) {
      const lastChildElement = childRefs.current.get(lastChild.issueId);
      if (lastChildElement) {
        const childMainCard = lastChildElement.querySelector(
          '[data-testid^="child-card-"]'
        );
        if (childMainCard) {
          const mainCardRect = childMainCard.getBoundingClientRect();
          lastChildY =
            mainCardRect.top - containerRect.top + mainCardRect.height / 2;
        }
      }
    }

    // Draw paths for each child that include both vertical and horizontal segments
    regularChildren.forEach((child, index) => {
      const childElement = childRefs.current.get(child.issueId);
      if (childElement) {
        const childRect = childElement.getBoundingClientRect();
        const childMainCard = childElement.querySelector(
          '[data-testid^="child-card-"]'
        );
        const mainCardRect =
          childMainCard?.getBoundingClientRect() || childRect;
        const connectionY =
          mainCardRect.top - containerRect.top + mainCardRect.height / 2;

        // Calculate the vertical line position at the center of parent's left side
        const verticalLineX = parentRelative.left - 5; // Move vertical line 5px left from the card
        const parentCenterY = parentRelative.top + parentRelative.height / 2;
        const horizontalEndX = childRect.left - containerRect.left;

        // Small curve radius for the corners
        const curveRadius = 5; // Very small radius for subtle curve

        // Create a path with horizontal segments and subtle corner curves at both ends
        const path = `M ${parentRelative.left} ${parentCenterY}
                     L ${verticalLineX + curveRadius} ${parentCenterY}
                     Q ${verticalLineX} ${parentCenterY}, ${verticalLineX} ${
          parentCenterY + curveRadius
        }
                     L ${verticalLineX} ${connectionY - curveRadius}
                     Q ${verticalLineX} ${connectionY}, ${
          verticalLineX + curveRadius
        } ${connectionY}
                     L ${horizontalEndX} ${connectionY}`;

        newLines.push({
          path,
          isFeature: false,
          isLastChild: index === regularChildren.length - 1
        });
      }
    });

    // Calculate lines for feature cards
    if (featureContainerRef.current) {
      const featureElements = featureContainerRef.current.querySelectorAll(
        'div[data-testid^="feature-card-"]:not([data-testid*="checkmark"]):not([data-testid*="title"]):not([data-testid*="risk-level"]):not([data-testid*="label"]):not([data-testid*="overdue"])'
      );

      if (featureElements.length > 0) {
        const verticalLineX = parentRelative.right + 5; // Move vertical line 5px right from the card
        const parentCenterY = parentRelative.top + parentRelative.height / 2;
        const curveRadius = 5; // Small radius for subtle curve

        // Calculate the vertical line extent
        let maxY = -Infinity;
        let lastFeatureCenterY = 0;
        let firstFeatureCenterY = Infinity;

        featureElements.forEach(featureEl => {
          const rect = featureEl.getBoundingClientRect();
          const centerY = rect.top - containerRect.top + rect.height / 2;
          maxY = Math.max(maxY, centerY);
          lastFeatureCenterY = centerY;
          firstFeatureCenterY = Math.min(firstFeatureCenterY, centerY);
        });

        // Draw the background vertical line first
        const verticalPadding = 4;
        const verticalPath = `M ${verticalLineX} ${
          parentCenterY + verticalPadding
        }
                            L ${verticalLineX} ${
          lastFeatureCenterY - verticalPadding
        }`;
        newLines.push({
          path: verticalPath,
          isFeature: true,
          isLastChild: false
        });

        // Start with parent connection
        const parentConnection = `M ${parentRelative.right} ${parentCenterY}
                                L ${
                                  verticalLineX - curveRadius
                                } ${parentCenterY}
                                Q ${verticalLineX} ${parentCenterY}, ${verticalLineX} ${
          parentCenterY + curveRadius
        }`;
        newLines.push({
          path: parentConnection,
          isFeature: true,
          isLastChild: false
        });

        // Draw vertical line segments between features with curves
        let prevY = parentCenterY + curveRadius;
        featureElements.forEach((featureEl, index) => {
          const rect = featureEl.getBoundingClientRect();
          const centerY = rect.top - containerRect.top + rect.height / 2;

          // Draw vertical line segment up to this feature
          const verticalSegment = `M ${verticalLineX} ${prevY}
                                 L ${verticalLineX} ${centerY - curveRadius}
                                 Q ${verticalLineX} ${centerY}, ${
            verticalLineX - curveRadius
          } ${centerY}`;
          newLines.push({
            path: verticalSegment,
            isFeature: true,
            isLastChild: false
          });

          prevY = centerY + curveRadius;
        });

        // Draw horizontal connections to each feature card
        featureElements.forEach((featureEl, index) => {
          const rect = featureEl.getBoundingClientRect();
          const centerY = rect.top - containerRect.top + rect.height / 2;
          const right = rect.right - containerRect.left;

          // Draw horizontal line starting after the curve, going to the right edge center
          const horizontalPath = `M ${verticalLineX - curveRadius} ${centerY}
                                H ${right}`;
          newLines.push({
            path: horizontalPath,
            isFeature: true,
            isLastChild: index === featureElements.length - 1
          });
        });
      }
    }

    setConnectorLines(newLines);
  }, [cardExpanded, childRelationships]);

  // Update connector lines when cards expand/collapse or on resize
  useEffect(() => {
    calculateConnectorLines();

    const observer = new ResizeObserver(() => {
      calculateConnectorLines();
    });

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    if (parentCardRef.current) {
      observer.observe(parentCardRef.current);
    }

    if (featureContainerRef.current) {
      observer.observe(featureContainerRef.current);
    }

    childRefs.current.forEach(childElement => {
      observer.observe(childElement);
    });

    return () => {
      observer.disconnect();
    };
  }, [calculateConnectorLines, cardExpanded]);

  // Function to get spacing for child cards
  const getChildSpacing = (index: number, isFeatureCard: boolean = false) => {
    // Special spacing for Feature cards to match Figma 10px gap
    // Feature cards are identified by being in the features array
    if (isFeatureCard) {
      return {
        position: 'relative' as const,
        paddingTop: index === 0 ? '0px' : '10px',
        marginBottom: '0'
      };
    }
    return {
      position: 'relative' as const,
      paddingTop: index === 0 ? '0.75rem' : '0.3rem',
      marginBottom: '0.3rem'
    };
  };

  // Handler for menu actions
  const handleMenuAction = async (key: any) => {
    const action = String(key);

    // Only allow actions for admin users
    if (!isAdmin) {
      setIsMenuOpen(false);
      return;
    }

    switch (action) {
      case 'edit':
        setIsEditModalOpen(true);
        setIsMenuOpen(false);
        break;
      case 'delete':
        setShowDeleteConfirmation(true);
        setIsMenuOpen(false);
        break;
      case 'markComplete':
        await handleMarkAsComplete();
        setIsMenuOpen(false);
        break;
      default:
        setIsMenuOpen(false);
        break;
    }
  };

  // Handler for marking events as complete
  const handleMarkAsComplete = async () => {
    try {
      // Update status to completed
      const updatePayload = {
        status: STATUS_VALUES.COMPLETE
      };

      await updateMoment(issueId, updatePayload);

      // Refresh the data to show updated status
      dispatch(actions.loadReleaseData({}));

      // Show success notification
      setNotification({
        message: 'Event marked as complete successfully!',
        variant: 'success'
      });
    } catch (error: any) {
      // Show error notification
      setNotification({
        message:
          error?.message ||
          'Failed to mark event as complete. Please try again.',
        variant: 'error'
      });
    }
  };

  // Handler for closing edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  // Handler for successful edit - refresh data seamlessly
  const handleEditSuccess = () => {
    // Refresh the moments data to get updated information
    dispatch(actions.loadReleaseData({}));
  };

  // Handler for delete confirmation
  const handleConfirmDelete = async () => {
    if (!issueId) {
      ToastQueue.negative('Unable to delete: Missing item ID', {
        timeout: 5000
      });
      setShowDeleteConfirmation(false);
      return;
    }

    setShowDeleteConfirmation(false);

    try {
      // Backend saveDeleteAuditHistory on DELETE /moments/{id}; no UI /audits POST.
      const result = await deleteMoment(issueId);

      if (result.success && result.status === 204) {
        // Show success toast
        ToastQueue.positive('Event has been deleted', { timeout: 5000 });

        // Clear events/parent moments cache to refresh the lists
        clearEventsCache();
        clearParentMomentsCache();

        // Refresh the roadmap data
        dispatch(actions.loadReleaseData({}));
      } else {
        // Show error toast
        ToastQueue.negative('Unable to delete Event', { timeout: 5000 });
        console.error('Delete failed:', result.error || result.message);
      }
    } catch (error) {
      // Show error toast
      ToastQueue.negative('Unable to delete Event', { timeout: 5000 });
      console.error('Error deleting Event:', error);
    }
  };

  // Handler for canceling deletion
  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  return (
    <>
      {/* Success/Error Notification Banner */}
      {notification && (
        <EnhancedAlertBanner
          variant={notification.variant}
          message={notification.message}
          onClose={() => setNotification(null)}
          autoHide={true}
          autoHideDelay={4000}
        />
      )}
      <div
        ref={parentCardRef}
        data-testid={`timeline-card-${issueId}`}
        data-unique-cardId={`timeline-card-${issueId}-${
          releasePhaseId ? releasePhaseId : ''
        }`}
        className={`${shouldApplyMmprStyling() ? styles.mmpr : ''} ${
          styles.card
        } ${isClosed ? styles.closed : ''}  ${
          shouldBeCompact ? styles.compact : ''
        } ${isEventType ? styles.isEvent : ''} ${
          isEolCard ? styles.eolCard : ''
        } ${
          isHighlighted || isTemporarilyHighlighted ? styles.highlighted : ''
        } ${actualShowOnTimeline ? styles.showOnTimeline : ''} ${
          isDateClicked && !isEventType ? styles.dateQuickEditActive : ''
        }`}
        style={{
          cursor: 'pointer',
          position: 'relative',
          zIndex: 'auto'
        }}
        onClick={highLightCards}
        onDoubleClick={handleDoubleClick}
        onMouseOver={isEventType ? () => setIsHovered(true) : undefined}
        onMouseOut={isEventType ? () => setIsHovered(false) : undefined}
        onMouseEnter={e => {
          e.stopPropagation();
          setIsCardHovered(true);
        }}
        onMouseLeave={e => {
          e.stopPropagation();
          setIsCardHovered(false);
        }}
      >
        {/* Three dots menu for Event cards - Only show for admins and ACTIVE status */}
        {isAdmin &&
          projectType === 'Event' &&
          status === STATUS_VALUES.ACTIVE &&
          issueId &&
          title &&
          (isCardHovered || isMenuOpen) && (
            <div
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 2
              }}
              onClick={e => e.stopPropagation()}
            >
              <MenuTrigger
                align="end"
                direction="bottom"
                shouldFlip={true}
                onOpenChange={isOpen => setIsMenuOpen(isOpen)}
              >
                <ActionButton
                  aria-label="More actions"
                  isQuiet
                  UNSAFE_style={{
                    display: 'flex',
                    width: '20px',
                    height: '20px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '5.5px',
                    background: 'transparent',
                    border: 'none',
                    padding: '2px',
                    minWidth: '20px',
                    minHeight: '20px'
                  }}
                >
                  <img
                    src={S2IconMore}
                    alt="More options"
                    style={{
                      width: '16px',
                      height: '16px',
                      display: 'block'
                    }}
                  />
                </ActionButton>
                <Menu
                  onAction={handleMenuAction}
                  UNSAFE_style={{
                    zIndex: 3,
                    background: '#FFFFFF',
                    border: 'none',
                    borderRadius: '6px',
                    boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.2)',
                    padding: '10px 5px',
                    minWidth: '140px',
                    maxWidth: '200px'
                  }}
                  UNSAFE_className="custom-context-menu"
                >
                  <Item key="edit">Edit</Item>
                  <Item key="delete">Delete</Item>
                  <Item key="markComplete">Mark as Complete</Item>
                </Menu>
              </MenuTrigger>
            </div>
          )}
        <div
          ref={dateContainerRef}
          data-testid={`timeline-card-date-${issueId}`}
          data-unique-dateContainerId={`timeline-card-date-${issueId}-${
            releasePhaseId ? releasePhaseId : ''
          }`}
          className={`${styles.dateContainer} ${
            shouldBeCompact ? styles.compactDate : ''
          } ${isOverdue ? styles.overdueDate : ''}
           ${
             isHighlighted || isTemporarilyHighlighted
               ? styles.highlightedDate
               : ''
           }
           ${isDateClicked ? styles.dateClicked : ''}`}
          onClick={e => {
            setIsDateClicked(!isDateClicked);
          }}
          onMouseOver={e => {
            const position = calculateTooltipPosition(
              dateContainerRef.current,
              252,
              600
            );
            setTooltipPlacement(position);
          }}
        >
          {renderDate()}
          {renderDateTooltip()}
        </div>
        <div
          ref={contentRef}
          data-testid={`timeline-card-content-${issueId}`}
          className={`${styles.content} ${
            shouldBeCompact ? styles.compactContent : ''
          }`}
          onClick={!isEventType ? openSlideOut : undefined}
        >
          {shouldBeCompact ? (
            renderCompactContent()
          ) : (
            <>
              <div className={styles.headingContainer}>
                {isEventType ? (
                  <TooltipTrigger
                    isOpen={openTooltip === 'title'}
                    onOpenChange={isOpen => {
                      if (!isOpen) setOpenTooltip(null);
                    }}
                    placement="top"
                  >
                    <div
                      className={styles.tooltipWrapper}
                      onMouseEnter={() => handleTooltipMouseEnter('title')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      <ActionButton
                        data-testid={`timeline-card-event-title-${issueId}`}
                        UNSAFE_className={`${styles.heading} ${styles.eventTitle}`}
                        onPress={highLightCards}
                        isQuiet
                        UNSAFE_style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          outline: 'none'
                        }}
                      >
                        <CardTitleDisplay
                          projectType={projectType}
                          releasePhase={releasePhase}
                          title={title}
                          displayTitle={displayTitle}
                          className={styles.headingText}
                        />
                      </ActionButton>
                    </div>
                    <Tooltip>{eolTooltipTitle}</Tooltip>
                  </TooltipTrigger>
                ) : (
                  <TooltipTrigger
                    isOpen={openTooltip === 'title'}
                    onOpenChange={isOpen => {
                      if (!isOpen) setOpenTooltip(null);
                    }}
                    placement="top"
                  >
                    <div
                      className={styles.tooltipWrapper}
                      onMouseEnter={() => handleTooltipMouseEnter('title')}
                      onMouseLeave={handleTooltipMouseLeave}
                    >
                      <ActionButton
                        data-testid={`timeline-card-title-${issueId}`}
                        UNSAFE_className={styles.heading}
                        onPress={openSlideOut}
                        isQuiet
                        UNSAFE_style={{
                          border: 'none',
                          backgroundColor: 'transparent',
                          outline: 'none',
                          height: 'fit-content'
                        }}
                      >
                        <CardTitleDisplay
                          projectType={projectType}
                          releasePhase={releasePhase}
                          title={title}
                          displayTitle={displayTitle}
                          className={styles.headingText}
                        />
                      </ActionButton>
                    </div>
                    <Tooltip>{eolTooltipTitle}</Tooltip>
                  </TooltipTrigger>
                )}
              </div>
              <div className={styles.icons}>
                <div className={styles.leftIcons}>
                  {' '}
                  {/* Show up to 3 product icons */}
                  {showInfoIcons && productIconsArray.length > 0 && (
                    <>
                      {productIconsArray.map((icon, index) => (
                        <TooltipTrigger
                          key={index}
                          isOpen={openTooltip === `product-${index}`}
                          onOpenChange={isOpen => {
                            if (!isOpen) setOpenTooltip(null);
                          }}
                          placement="top"
                        >
                          <div
                            className={styles.tooltipWrapper}
                            onMouseEnter={() =>
                              handleTooltipMouseEnter(`product-${index}`)
                            }
                            onMouseLeave={handleTooltipMouseLeave}
                          >
                            <ActionButton
                              data-testid={`timeline-card-product-icon-${index}-${issueId}`}
                              isQuiet
                              UNSAFE_className={styles.iconActionButton}
                              aria-label={
                                Array.isArray(products) ? products[index] : ''
                              }
                            >
                              <Image
                                src={icon}
                                alt={
                                  Array.isArray(products) ? products[index] : ''
                                }
                                UNSAFE_className={styles['categories-icon']}
                                onError={handleImageError}
                              />
                            </ActionButton>
                          </div>
                          <Tooltip>
                            {Array.isArray(products) ? products[index] : ''}
                          </Tooltip>
                        </TooltipTrigger>
                      ))}
                    </>
                  )}
                </div>
                <div
                  className={styles.rightIcons}
                  onClick={e => e.stopPropagation()}
                >
                  {showInfoIcons &&
                    !isClosed &&
                    riskLevel &&
                    riskLevel !== 'NA' && (
                      <TooltipTrigger
                        isOpen={openTooltip === 'risk'}
                        onOpenChange={isOpen => {
                          if (!isOpen) setOpenTooltip(null);
                        }}
                        placement="top"
                      >
                        <div
                          className={styles.tooltipWrapper}
                          onMouseEnter={() => handleTooltipMouseEnter('risk')}
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ActionButton
                            data-testid={`timeline-card-risk-level-${issueId}`}
                            isQuiet
                            UNSAFE_className={styles.iconActionButton}
                            aria-label={`Risk Level: ${getRiskLevelDisplayName(
                              riskLevel
                            )}`}
                          >
                            {getStatusIcon()}
                          </ActionButton>
                        </div>
                        <Tooltip>
                          <RiskLevelTooltipContent
                            riskLevel={riskLevel}
                            className={styles.riskLevelText}
                          />
                        </Tooltip>
                      </TooltipTrigger>
                    )}
                  {showInfoIcons &&
                    releasePhase &&
                    getReleasePhaseIcon(releasePhase) && (
                      <TooltipTrigger
                        isOpen={
                          openTooltip ===
                          `label-${getReleasePhaseIcon(releasePhase)}`
                        }
                        onOpenChange={isOpen => {
                          if (!isOpen) setOpenTooltip(null);
                        }}
                        placement="top"
                      >
                        <div
                          className={styles.tooltipWrapper}
                          onMouseEnter={() =>
                            handleTooltipMouseEnter(
                              `label-${getReleasePhaseIcon(releasePhase)}`
                            )
                          }
                          onMouseLeave={handleTooltipMouseLeave}
                        >
                          <ActionButton
                            data-testid={`timeline-card-label-${getReleasePhaseIcon(
                              releasePhase
                            )}-${issueId}`}
                            isQuiet
                            UNSAFE_className={styles.iconActionButton}
                            aria-label={releasePhase}
                          >
                            {getReleasePhaseIcon(releasePhase) === 'beta' ? (
                              <LegendBetaMiniIcon />
                            ) : (
                              <LegendPrivateBetaMiniIcon />
                            )}
                          </ActionButton>
                        </div>
                        <Tooltip>{releasePhase}</Tooltip>
                      </TooltipTrigger>
                    )}

                  {isExpandable && (
                    <TooltipTrigger
                      isOpen={openTooltip === 'expand'}
                      onOpenChange={isOpen => {
                        if (!isOpen) setOpenTooltip(null);
                      }}
                      placement="top"
                    >
                      <div
                        className={styles.tooltipWrapper}
                        onMouseEnter={() => handleTooltipMouseEnter('expand')}
                        onMouseLeave={handleTooltipMouseLeave}
                      >
                        <ActionButton
                          data-testid={`timeline-card-expand-button-${issueId}`}
                          isQuiet
                          UNSAFE_className={styles.iconActionButton}
                          aria-label="Expand"
                          onPress={() => {
                            const newExpandedState = !cardExpanded;
                            setCardExpanded(newExpandedState);

                            // ✅ Save manually expanded cards to sessionStorage (per-instance key in Must Wins view)
                            const cardCompositeKey = releasePhaseId
                              ? `${issueId}-${releasePhaseId}-${actualShowOnTimeline}`
                              : issueId;
                            const expandKey =
                              mustWinInstanceKey ?? cardCompositeKey;

                            const expandedCards = JSON.parse(
                              sessionStorage.getItem(
                                'manually_expanded_cards'
                              ) || '[]'
                            );

                            if (newExpandedState) {
                              // Add to expanded list
                              if (!expandedCards.includes(expandKey)) {
                                expandedCards.push(expandKey);
                                sessionStorage.setItem(
                                  'manually_expanded_cards',
                                  JSON.stringify(expandedCards)
                                );
                              }
                            } else {
                              // Remove from expanded list
                              const filtered = expandedCards.filter(
                                id => id !== expandKey
                              );
                              sessionStorage.setItem(
                                'manually_expanded_cards',
                                JSON.stringify(filtered)
                              );
                            }
                          }}
                        >
                          <Image height="size-225" src={ExpandIcon} alt="" />
                        </ActionButton>
                      </div>
                      <Tooltip>{cardExpanded ? 'Collapse' : 'Expand'}</Tooltip>
                    </TooltipTrigger>
                  )}

                  {showInfoIcons && (
                    <RevenueImpactIcon
                      issueId={issueId}
                      revenueImpact={revenueImpact}
                      openTooltip={openTooltip}
                      setOpenTooltip={setOpenTooltip}
                      handleTooltipMouseEnter={handleTooltipMouseEnter}
                      handleTooltipMouseLeave={handleTooltipMouseLeave}
                      iconActionButtonClass={styles.iconActionButton}
                      tooltipWrapperClass={styles.tooltipWrapper}
                      isCompact={false}
                    />
                  )}
                </div>
              </div>
            </>
          )}
          {renderTooltip()}
        </div>
        {/* Checkmark removed - no longer showing for completed cards */}
        {/* {isClosed && !isHovered && (
          <div
            className={styles.checkmark}
            style={{ zIndex: 2 }}
            data-testid={`timeline-card-checkmark-${issueId}`}
            onMouseEnter={() => isEventType && setIsHovered(false)}
          >
            <CheckMarkCircleIcon width="18" height="18" />
          </div>
        )} */}
        {isOverdue && !isHovered && showInfoIcons && (
          <div
            className={styles.checkmark}
            style={{ zIndex: 2 }}
            data-testid={`timeline-card-overdue-${issueId}`}
            onMouseEnter={() => isEventType && setIsHovered(false)}
          >
            <Image
              src={alertTriangle}
              alt="Overdue alert"
              height="18px"
              width="18px"
              UNSAFE_style={{
                minWidth: '18px',
                minHeight: '18px'
              }}
            />
          </div>
        )}

        {isEventType && (isHovered || isHighlighted) && (
          <div
            className={styles.relationshipCount}
            style={{ zIndex: 2 }}
            data-testid={`timeline-card-relationship-count-${issueId}`}
            onMouseLeave={() => setIsHovered(false)}
          >
            {implementedCount}
          </div>
        )}
      </div>

      {cardExpanded && isExpandable && (
        <div
          ref={containerRef}
          data-testid={`timeline-card-children-container-${issueId}`}
          style={{
            marginTop: '-12px',
            marginBottom: '10px',
            display: 'flex',
            flexDirection: 'column',
            position: 'relative'
          }}
        >
          {/* SVG container for connector lines */}
          <svg
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              pointerEvents: 'none',
              overflow: 'visible',
              zIndex: 1
            }}
          >
            {connectorLines.map((line, index) => (
              <path
                key={index}
                d={line.path}
                stroke="#96cefd"
                strokeWidth="2"
                fill="none"
                style={{
                  zIndex: line.isFeature ? 2 : 1
                }}
              />
            ))}
          </svg>

          {/* Left-side container for regular child cards */}
          {childRelationships.some(
            child => child.projectType !== MOMENT_TYPES.FEATURE
          ) && (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                zIndex: 2
              }}
            >
              {childRelationships
                .filter(child => child.projectType !== MOMENT_TYPES.FEATURE)
                .map((child, index, filteredChildren) => {
                  const childDocs = child.links?.filter(
                    link => link.type === 'DOCUMENTATION'
                  );

                  const linkWithReference = childDocs?.find(
                    link => link?.reference !== null
                  );
                  const hasDocLink = linkWithReference?.reference;

                  const childWithParentDate = {
                    ...child,
                    features: child.features || [], // Include features from child
                    relationships: [
                      ...(child.relationships || []),
                      {
                        type: 'is child of',
                        value: issueId,
                        targetDate: dates?.targetStart
                      }
                    ]
                  };

                  return (
                    <div
                      key={child.issueId}
                      ref={el => {
                        if (el) childRefs.current.set(child.issueId, el);
                      }}
                      className={`${styles.childCard} ${
                        isSmall ? styles.compact : ''
                      } 
                      ${filteredChildren.length === 1 ? styles.lastCard : ''} ${
                        index === 0 ? styles.firstCard : ''
                      }`}
                      style={{
                        cursor: hasDocLink ? 'pointer' : 'default',
                        marginLeft: '0' // Ensure no extra margin
                      }}
                      onClick={e => {
                        e.stopPropagation();
                      }}
                    >
                      <ChildCard
                        key={child.issueId}
                        card={childWithParentDate}
                        type={type}
                        isChild={true}
                        isSmall={isSmall}
                        level={0}
                        isLast={index === filteredChildren.length - 1}
                        parentPriority={priority}
                        isQuarterExpanded={isQuarterExpanded}
                        isUnNested={isUnNested}
                        canEditFeatures={canEditFeatures}
                      />
                    </div>
                  );
                })}
            </div>
          )}

          {/* Right-side container for feature cards */}
          {orderedFeatures && orderedFeatures.length > 0 && (
            <div
              ref={featureContainerRef}
              style={{
                position: 'relative',
                paddingTop: '23px',
                marginTop: '-12px',
                display: 'flex',
                flexDirection: 'column',
                marginLeft: 'auto', // Push to the right side
                zIndex: 1
              }}
            >
              {orderedFeatures.map((child, index, filteredChildren) => {
                const childDocs = child.links?.filter(
                  link => link.type === 'DOCUMENTATION'
                );

                const linkWithReference = childDocs?.find(
                  link => link?.reference !== null
                );
                const hasDocLink = linkWithReference?.reference;

                // Use featureId or issueId for unique identification
                const childUniqueId = child.featureId || child.issueId;
                const isDragging = featureReorder.draggedId === childUniqueId;
                const isDragOver = featureReorder.dragOverId === childUniqueId;

                const childWithParentDate = {
                  ...child,
                  features: child.features || [], // Include features from child
                  relationships: [
                    ...(child.relationships || []),
                    {
                      type: 'is child of',
                      value: issueId,
                      targetDate: dates?.targetStart
                    }
                  ],
                  // Provide parent context for GA badge comparison in FeatureCard
                  parentReleasePhase: releasePhase,
                  parentDates: dates,
                  parentShowOnTimeline: actualShowOnTimeline // Pass parent's showOnTimeline flag
                };

                const showDropLineAbove =
                  featureReorder.dropPosition?.index === index;
                const showDropLineBelow =
                  featureReorder.dropPosition?.index === index + 1;
                const isJustReordered =
                  lastReorderedFeatureId === childUniqueId;
                const isDeniedTarget =
                  draggedProduct !== null && child.productId !== draggedProduct;

                return (
                  <div
                    key={childUniqueId}
                    data-testid={`timeline-card-feature-wrapper-${childUniqueId}`}
                    title={
                      canEditFeatures && !canReorderFeatures
                        ? 'Feature reordering is not available for this moment type'
                        : undefined
                    }
                    onDragOver={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => {
                            if (child.productId !== draggedProductIdRef.current)
                              return;
                            featureReorder.handleDragOver(
                              e,
                              childUniqueId,
                              orderedFeatures
                            );
                          }
                        : undefined
                    }
                    onDragLeave={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => featureReorder.handleDragLeave(e, childUniqueId)
                        : undefined
                    }
                    onDrop={
                      DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                        ? e => {
                            if (child.productId !== draggedProductIdRef.current)
                              return;
                            featureReorder.handleDrop(
                              e,
                              childUniqueId,
                              orderedFeatures
                            );
                          }
                        : undefined
                    }
                    style={{
                      ...getChildSpacing(index, true), // true = is a feature card
                      position: 'relative',
                      display: 'flex',
                      alignItems: 'stretch',
                      zIndex: isDragging ? 10 : 'auto',
                      opacity: isDeniedTarget ? 0.35 : 1,
                      cursor: isDeniedTarget ? 'not-allowed' : undefined,
                      transition: 'opacity 0.15s ease'
                    }}
                    className={`${styles.featureRow} ${
                      isDragOver ? styles.featureDragOver : ''
                    }`.trim()}
                  >
                    {/* Blue drop indicator - above this row */}
                    {showDropLineAbove && (
                      <div
                        className={styles.featureDropIndicator}
                        style={{ top: 0, left: 0, right: 0 }}
                      />
                    )}
                    <div
                      draggable={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                      }
                      onDragStart={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                          ? e => {
                              draggedProductIdRef.current =
                                child.productId ?? null;
                              featureReorder.handleDragStart(e, childUniqueId);
                            }
                          : undefined
                      }
                      onDragEnd={() => {
                        draggedProductIdRef.current = null;
                        featureReorder.handleDragEnd();
                      }}
                      onKeyDown={
                        DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                          ? e => {
                              // Keyboard reorder: only move within the same product
                              const sameProductItems = orderedFeatures.filter(
                                (f: any) => f.productId === child.productId
                              );
                              featureReorder.handleRowKeyDown(
                                e,
                                childUniqueId,
                                sameProductItems
                              );
                            }
                          : undefined
                      }
                      style={{
                        flex: 1,
                        minWidth: 0,
                        transition:
                          'opacity 0.25s cubic-bezier(0.25, 0.4, 0.25, 1)',
                        cursor:
                          DRAG_DROP_REORDER_ENABLED && canReorderFeatures
                            ? isDeniedTarget
                              ? 'not-allowed'
                              : 'grab'
                            : 'default'
                      }}
                      className={isDragging ? styles.featurePlaceholder : ''}
                    >
                      {isDragging ? (
                        <div className={styles.featurePlaceholderInner} />
                      ) : (
                        <FeatureCard
                          card={childWithParentDate}
                          type={type}
                          isChild={true}
                          level={2}
                          parentPriority={priority}
                          isQuarterExpanded={isQuarterExpanded}
                          isLast={index === filteredChildren.length - 1}
                          isJustReordered={isJustReordered}
                        />
                      )}
                    </div>
                    {/* Blue drop indicator - below this row */}
                    {showDropLineBelow && (
                      <div
                        className={styles.featureDropIndicator}
                        style={{ bottom: 0, left: 0, right: 0 }}
                      />
                    )}
                  </div>
                );
              })}

              {/* After-last drop zone - accepts drop below last feature */}
              {DRAG_DROP_REORDER_ENABLED &&
                canReorderFeatures &&
                orderedFeatures.length > 0 && (
                  <div
                    data-testid={`timeline-card-feature-wrapper-${AFTER_LAST_DROP_TARGET}`}
                    onDragOver={e => {
                      const lastFeature =
                        orderedFeatures[orderedFeatures.length - 1];
                      if (
                        lastFeature?.productId !== draggedProductIdRef.current
                      )
                        return;
                      featureReorder.handleDragOver(
                        e,
                        AFTER_LAST_DROP_TARGET,
                        orderedFeatures
                      );
                    }}
                    onDragLeave={e =>
                      featureReorder.handleDragLeave(e, AFTER_LAST_DROP_TARGET)
                    }
                    onDrop={e => {
                      const lastFeature =
                        orderedFeatures[orderedFeatures.length - 1];
                      if (
                        lastFeature?.productId !== draggedProductIdRef.current
                      )
                        return;
                      featureReorder.handleDrop(
                        e,
                        AFTER_LAST_DROP_TARGET,
                        orderedFeatures
                      );
                    }}
                    className={styles.featureAfterLastDropZone}
                    style={{
                      minHeight:
                        featureReorder.dragOverId === AFTER_LAST_DROP_TARGET
                          ? 24
                          : 12
                    }}
                  >
                    {featureReorder.dragOverId === AFTER_LAST_DROP_TARGET &&
                      featureReorder.dropPosition?.index ===
                        orderedFeatures.length && (
                        <div
                          className={styles.featureDropIndicator}
                          style={{
                            top: 0,
                            left: 0,
                            right: 0
                          }}
                        />
                      )}
                  </div>
                )}
            </div>
          )}
        </div>
      )}

      {/* Edit Event Modal */}
      {isEditModalOpen && (
        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={handleCloseEditModal}
          onSuccess={handleEditSuccess}
          itemType={projectType || 'Event'}
          issueId={issueId}
          isOverdue={isOverdue} // ✅ Pass isOverdue status to highlight dates in red
          initialValues={{
            title: title || '',
            eventName: title || '',
            name: title || '',
            projectType: projectType || '',
            momentType: projectType || '',
            startDate: dates?.targetStart || null,
            endDate: dates?.targetEnd || null,
            // Pass the mapped scheduleRange and dateRange for month-based dates
            scheduleRange: scheduleRange || null,
            dateRange: dateRange || null,
            // Also pass the raw dates object for fallback processing
            dates: dates || null,
            description: reportingSummary || '',
            id: issueId || '',
            categories: categories || '',
            products: products || '',
            labels: labels || [],
            riskLevel: riskLevel || '',
            status: status || '',
            priority: priority || '',
            releasePhase: releasePhase || ''
          }}
        />
      )}

      {/* Delete Confirmation Dialog - Using Portal to render at body level */}
      {showDeleteConfirmation &&
        ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}
            onClick={e => {
              // Close if clicking on backdrop
              if (e.target === e.currentTarget) {
                handleCancelDelete();
              }
            }}
          >
            <div
              style={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '400px',
                width: '90%',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
              }}
            >
              <h2
                style={{
                  color: '#000',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '20px',
                  fontWeight: 500,
                  lineHeight: 1.3,
                  margin: '0 0 16px 0'
                }}
              >
                Delete Event?
              </h2>
              <p
                style={{
                  color: '#6d6d6d',
                  fontFamily: "'Adobe Clean', Arial, sans-serif",
                  fontSize: '14px',
                  lineHeight: 1.5,
                  margin: '0 0 24px 0'
                }}
              >
                Are you sure you want to delete this event? This action cannot
                be undone.
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px'
                }}
              >
                <button
                  onClick={handleCancelDelete}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#e6e6e6',
                    color: '#222222',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px',
                    background: '#d7373f',
                    color: '#ffffff',
                    fontFamily: "'Adobe Clean', Arial, sans-serif",
                    fontSize: '14px',
                    fontWeight: 700,
                    border: 'none',
                    padding: '6px 16px 8px 16px',
                    cursor: 'pointer'
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(TimelineCard);
